import { useEffect, useState } from "react";
import {
  login,
  loginWithGoogle,
  resetPassword,
  registerAndSendVerification,
  checkVerificationAndDelete,
  logout,
  sendVerificationEmail,
  refreshUser,
} from "./auth-logic";
import { useNavigate, Link, useSearchParams, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "./AuthContext";
import { InputField } from "../common/InputField";
import bgHero from "../assets/bghero1.jpg";
import vionixWhite from "../assets/vionixwhite.png";
const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

const resetPasswordSchema = z.object({
  email: z.string().email("Invalid email address"),
});

const registerSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

export const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const continueUrl = searchParams.get("continue");
  const { user } = useAuth();
  const [formMode, setFormMode] = useState("login");
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showInputs, setShowInputs] = useState(false);

  // verification modal state
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [isResending, setIsResending] = useState(false);

  // LOGIN form
  const {
    handleSubmit: handleSubmitLogin,
    control: controlLogin,
    setValue: setValueLogin,
    formState: { errors: errorsLogin, isSubmitting: isSubmittingLogin },
  } = useForm({
    resolver: zodResolver(loginSchema),
    mode: "onTouched",
  });

  // RESET form
  const {
    handleSubmit: handleSubmitReset,
    control: controlReset,
    formState: { errors: errorsReset, isSubmitting: isSubmittingReset },
  } = useForm({
    resolver: zodResolver(resetPasswordSchema),
    mode: "onTouched",
  });

  // REGISTER form
  const {
    handleSubmit: handleSubmitReg,
    control: controlReg,
    formState: { errors: errorsReg, isSubmitting: isSubmittingReg },
  } = useForm({
    resolver: zodResolver(registerSchema),
    mode: "onTouched",
  });

  // keep the page from scrolling while modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  // Fill email from localStorage if present (after verification)
  useEffect(() => {
    if (formMode === "login") {
      const verifiedEmail = localStorage.getItem("verifiedEmail");
      if (verifiedEmail) {
        setValueLogin("email", verifiedEmail);
        localStorage.removeItem("verifiedEmail");
        setShowInputs(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formMode]);

  const location = useLocation();
  const from = location.state?.from || "/Profile";

  // Only redirect when user exists and email is verified
  useEffect(() => {
    if (user && user.emailVerified) {
      if (continueUrl) {
        window.location.href = continueUrl;
      } else {
        navigate(from, { replace: true });
      }
    }
  }, [user, navigate, continueUrl, from]);

  // Reset messages on mode change
  useEffect(() => {
    setErrorMessage(null);
    setSuccessMessage(null);
  }, [formMode]);

  const handleModeChange = (mode) => {
    setFormMode(mode);
    setErrorMessage(null);
    setSuccessMessage(null);
    if (mode === "login") {
      setShowInputs(false);
    }
  };

  // Error normalizer (friendly English messages)
  const getFriendlyError = (error) => {
    if (!error) return "An error occurred. Please try again.";
    if (typeof error === "string" && !error.startsWith("Firebase: Error"))
      return error;

    const message = error && error.message ? String(error.message) : "";
    let code = error && error.code ? String(error.code) : "";

    if (!code && message) {
      const m = message.match(/\(auth\/([^)]+)\)/i);
      if (m && m[1]) code = m[1];
    }

    const c = (code || "").toLowerCase();
    const mLower = message.toLowerCase();

    if (
      c.includes("email-already-in-use") ||
      mLower.includes("email-already-in-use")
    ) {
      return "Email already in use. Please log in or use a different email.";
    }
    if (
      c.includes("invalid-email") ||
      mLower.includes("invalid-email") ||
      mLower.includes("invalid email")
    ) {
      return "Invalid email format.";
    }
    if (c.includes("weak-password") || mLower.includes("weak-password")) {
      return "Password is too weak. Please use a stronger password.";
    }
    if (c.includes("user-not-found") || mLower.includes("user-not-found")) {
      return "Email not found. Please register first.";
    }
    if (c.includes("wrong-password") || mLower.includes("wrong-password")) {
      return "Incorrect password. Please try again.";
    }
    if (
      c.includes("too-many-requests") ||
      mLower.includes("too-many-requests")
    ) {
      return "Too many attempts. Please try again later.";
    }
    if (
      c.includes("invalid-credential") ||
      mLower.includes("invalid-credential") ||
      mLower.includes("invalid credential")
    ) {
      return "Invalid credentials. Please check your login details.";
    }
    if (
      mLower.includes("belum diverifikasi") ||
      mLower.includes("verifikasi") ||
      mLower.includes("not verified") ||
      mLower.includes("verification")
    ) {
      return "Email not verified. Please check your inbox.";
    }
    if (message && !message.toLowerCase().startsWith("firebase: error")) {
      return message;
    }
    return "An error occurred. Please try again or contact support if the problem persists.";
  };

  // handle login
  const handleLogin = async (data) => {
    try {
      setErrorMessage(null);
      setSuccessMessage(null);

      const signedUser = await login(data.email, data.password);

      // If signed-in but not verified -> show modal
      if (signedUser && !signedUser.emailVerified) {
        setVerificationEmail(signedUser.email || data.email || "");
        setShowVerificationModal(true);
        setErrorMessage("Please verify your email.");
        return;
      }

      // Otherwise navigate
      if (continueUrl) {
        window.location.href = continueUrl;
      } else {
        navigate("/Profile");
      }
    } catch (error) {
      console.error("Login error:", error);
      const friendly = getFriendlyError(error);
      setErrorMessage(friendly);

      // show modal if error indicates unverified email (support both languages)
      const msgLower = (error?.message || "").toLowerCase();
      if (
        msgLower.includes("belum diverifikasi") ||
        msgLower.includes("verifikasi") ||
        msgLower.includes("not verified") ||
        msgLower.includes("verification")
      ) {
        setVerificationEmail(data?.email || "");
        setShowVerificationModal(true);
      }
    }
  };

  const handleGoogleLogin = async () => {
    setLoadingGoogle(true);
    setErrorMessage(null);
    try {
      await loginWithGoogle();
      if (continueUrl) {
        window.location.href = continueUrl;
      } else {
        navigate("/Profile");
      }
    } catch (error) {
      console.error("Google login error:", error);
      setErrorMessage(getFriendlyError(error));
    } finally {
      setLoadingGoogle(false);
    }
  };

  const handleResetPassword = async (data) => {
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await resetPassword(data.email);
      setSuccessMessage("Password reset link has been sent.");
    } catch (error) {
      console.error("Reset password error:", error);
      setErrorMessage(getFriendlyError(error));
    }
  };

  const handleRegister = async (data) => {
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const { message } = await registerAndSendVerification(
        data.email,
        data.name,
        data.password
      );

      setSuccessMessage(
        message || "Registration successful. Check your email to verify."
      );

      // schedule verification check & auto-back-to-login
      setTimeout(async () => {
        try {
          await checkVerificationAndDelete();
        } catch (e) {
          console.warn("checkVerificationAndDelete error:", e);
        }
      }, 120000);

      setTimeout(() => {
        setFormMode("login");
        setShowInputs(false);
      }, 5000);
    } catch (error) {
      console.error("Register error:", error);
      setErrorMessage(getFriendlyError(error));
    }
  };

  // resend verification handler for modal
  const handleResendVerification = async () => {
    setIsResending(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      // refresh current signed-in user (we keep them signed in on unverified login)
      const current = await refreshUser();
      if (!current) {
        setErrorMessage("Unable to send verification. Please log in again.");
        setIsResending(false);
        return;
      }

      await sendVerificationEmail(current);
      setSuccessMessage("Verification email sent.");
    } catch (err) {
      console.error("Resend verification error:", err);
      setErrorMessage(getFriendlyError(err));
    } finally {
      setIsResending(false);
    }
  };

  return (

    <div className="min-h-screen flex flex-col items-center justify-center relative font-sans overflow-hidden">
      {/* Background Image & Overlay */}
      <img
        src={bgHero}
        alt="Background"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 to-gray-900/60"></div>

      {/* Content */}
      <div className="relative z-10 max-w-md w-full px-4 flex flex-col items-center">
        
        {/* Logo */}
        <img
          src={vionixWhite}
          alt="VionixAsia"
          className="h-16 lg:h-20 object-contain mb-8 opacity-90"
        />

        <div className="text-center mb-8 w-full">
          <h2 className="text-xl lg:text-2xl font-bold text-white uppercase tracking-[0.2em]">
            {formMode === 'login' && 'Welcome Back'}
            {formMode === 'register' && 'Join Us'}
            {formMode === 'forgot-password' && 'Recovery'}
          </h2>
          <p className="text-gray-200 text-sm max-w-xs mx-auto leading-relaxed">
            {formMode === 'login' && 'Sign in to manage your account and access your VIONIXASIA services.'}
            {formMode === 'register' && 'Create an account to start tracking and analyzing.'}
            {formMode === 'forgot-password' && 'Enter your email to receive a password reset link.'}
          </p>
        </div>

        <AnimatePresence mode="wait">
          {formMode === "login" && (
            <motion.div
              key="login"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="w-full"
            >
              {!showInputs ? (
                <div className="space-y-4">
                  <button
                    onClick={handleGoogleLogin}
                    disabled={loadingGoogle}
                    className="w-full bg-white/10 backdrop-blur-sm border border-white/20 text-white py-4 text-xs font-bold uppercase tracking-[0.2em] hover:bg-white/20 transition-colors rounded-xl flex items-center justify-center gap-3"
                  >
                    {loadingGoogle ? (
                      "Processing..."
                    ) : (
                      <>
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.51h5.84c-.25 1.37-1.02 2.53-2.16 3.31v2.75h3.49c2.04-1.88 3.23-4.65 3.23-7.77z" />
                          <path d="M12 23c2.97 0 5.46-1.01 7.28-2.73l-3.49-2.75c-1.01.68-2.3 1.08-3.79 1.08-2.91 0-5.37-1.97-6.25-4.61H2.36v2.9C4.17 20.99 7.83 23 12 23z" />
                          <path d="M5.75 14.24c-.22-.66-.34-1.37-.34-2.11s.12-1.45.34-2.11V7.12H2.36C1.49 8.71 1 10.34 1 12s.49 3.29 1.36 4.88l3.39-2.64z" />
                          <path d="M12 5.02c1.61 0 3.04.55 4.18 1.62l3.13-3.13C17.46 1.99 14.97 1 12 1 7.83 1 4.17 3.01 2.36 6.88l3.39 2.64c.88-2.64 3.34-4.5 6.25-4.5z" />
                        </svg>
                        Sign in with Google
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={() => setShowInputs(true)}
                    className="w-full bg-white text-black py-4 text-xs font-bold uppercase tracking-[0.2em] hover:bg-gray-200 transition-colors rounded-xl"
                  >
                    Sign in with Email
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmitLogin(handleLogin)} className="space-y-6">
                  <Controller
                    name="email"
                    control={controlLogin}
                    defaultValue=""
                    render={({ field }) => (
                      <div className="relative">
                        <input
                          {...field}
                          type="email"
                          placeholder="Email"
                          className="w-full px-0 py-3 text-center text-lg tracking-wider placeholder-white/30 bg-transparent text-white outline-none border-b border-white/20 focus:border-white transition-colors"
                        />
                        {errorsLogin.email && (
                          <p className="text-[10px] text-red-400 text-center mt-1 uppercase tracking-widest">{errorsLogin.email.message}</p>
                        )}
                      </div>
                    )}
                  />
                  
                  <Controller
                    name="password"
                    control={controlLogin}
                    defaultValue=""
                    render={({ field }) => (
                      <div className="relative">
                        <input
                          {...field}
                          type="password"
                          placeholder="Password"
                          className="w-full px-0 py-3 text-center text-lg tracking-wider placeholder-white/30 bg-transparent text-white outline-none border-b border-white/20 focus:border-white transition-colors"
                        />
                         {errorsLogin.password && (
                          <p className="text-[10px] text-red-400 text-center mt-1 uppercase tracking-widest">{errorsLogin.password.message}</p>
                        )}
                      </div>
                    )}
                  />

                  {errorMessage && (
                    <p className="text-[10px] text-red-400 text-center uppercase tracking-widest font-bold">
                      {errorMessage}
                    </p>
                  )}

                  <div className="pt-4 space-y-4">
                    <button
                      type="submit"
                      disabled={isSubmittingLogin}
                      className="w-full bg-white text-black py-4 text-xs font-bold uppercase tracking-[0.2em] hover:bg-gray-200 transition-colors rounded-xl"
                    >
                      {isSubmittingLogin ? "Processing..." : "Login"}
                    </button>

                    <div className="flex justify-between items-center text-[10px] uppercase tracking-widest text-gray-400 px-2">
                      <button type="button" onClick={() => handleModeChange("forgot-password")} className="hover:text-white transition-colors">
                        Forgot Password?
                      </button>
                      <button type="button" onClick={() => handleModeChange("register")} className="hover:text-white transition-colors">
                        Register
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </motion.div>
          )}

          {formMode === "register" && (
            <motion.div
              key="register"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="w-full"
            >
              <form onSubmit={handleSubmitReg(handleRegister)} className="space-y-6">
                <Controller
                  name="name"
                  control={controlReg}
                  defaultValue=""
                  render={({ field }) => (
                    <div className="relative">
                      <input
                        {...field}
                        type="text"
                        placeholder="Full Name"
                        className="w-full px-0 py-3 text-center text-lg tracking-wider placeholder-white/30 bg-transparent text-white outline-none border-b border-white/20 focus:border-white transition-colors"
                      />
                      {errorsReg.name && (
                        <p className="text-[10px] text-red-400 text-center mt-1 uppercase tracking-widest">{errorsReg.name.message}</p>
                      )}
                    </div>
                  )}
                />

                <Controller
                  name="email"
                  control={controlReg}
                  defaultValue=""
                  render={({ field }) => (
                    <div className="relative">
                      <input
                        {...field}
                        type="email"
                        placeholder="Email"
                        className="w-full px-0 py-3 text-center text-lg tracking-wider placeholder-white/30 bg-transparent text-white outline-none border-b border-white/20 focus:border-white transition-colors"
                      />
                      {errorsReg.email && (
                        <p className="text-[10px] text-red-400 text-center mt-1 uppercase tracking-widest">{errorsReg.email.message}</p>
                      )}
                    </div>
                  )}
                />

                <Controller
                  name="password"
                  control={controlReg}
                  defaultValue=""
                  render={({ field }) => (
                    <div className="relative">
                      <input
                        {...field}
                        type="password"
                        placeholder="Password"
                        className="w-full px-0 py-3 text-center text-lg tracking-wider placeholder-white/30 bg-transparent text-white outline-none border-b border-white/20 focus:border-white transition-colors"
                      />
                      {errorsReg.password && (
                        <p className="text-[10px] text-red-400 text-center mt-1 uppercase tracking-widest">{errorsReg.password.message}</p>
                      )}
                    </div>
                  )}
                />

                {errorMessage && (
                  <p className="text-[10px] text-red-400 text-center uppercase tracking-widest font-bold">
                    {errorMessage}
                  </p>
                )}
                {successMessage && (
                  <p className="text-[10px] text-green-400 text-center uppercase tracking-widest font-bold">
                    {successMessage}
                  </p>
                )}

                <div className="pt-4 space-y-4">
                  <button
                    type="submit"
                    disabled={isSubmittingReg}
                    className="w-full bg-white text-black py-4 text-xs font-bold uppercase tracking-[0.2em] hover:bg-gray-200 transition-colors rounded-xl"
                  >
                    {isSubmittingReg ? "Processing..." : "Register"}
                  </button>

                  <div className="text-center">
                    <button 
                      type="button" 
                      onClick={() => handleModeChange("login")} 
                      className="text-[10px] uppercase tracking-widest text-gray-400 hover:text-white transition-colors"
                    >
                      Back to Login
                    </button>
                  </div>
                </div>
              </form>
            </motion.div>
          )}

          {formMode === "forgot-password" && (
            <motion.div
              key="forgot-password"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="w-full"
            >
              <form onSubmit={handleSubmitReset(handleResetPassword)} className="space-y-6">
                <Controller
                  name="email"
                  control={controlReset}
                  defaultValue=""
                  render={({ field }) => (
                    <div className="relative">
                      <input
                        {...field}
                        type="email"
                        placeholder="Enter your email"
                        className="w-full px-0 py-3 text-center text-lg tracking-wider placeholder-white/30 bg-transparent text-white outline-none border-b border-white/20 focus:border-white transition-colors"
                      />
                      {errorsReset.email && (
                        <p className="text-[10px] text-red-400 text-center mt-1 uppercase tracking-widest">{errorsReset.email.message}</p>
                      )}
                    </div>
                  )}
                />

                {errorMessage && (
                  <p className="text-[10px] text-red-400 text-center uppercase tracking-widest font-bold">
                    {errorMessage}
                  </p>
                )}
                {successMessage && (
                  <p className="text-[10px] text-green-400 text-center uppercase tracking-widest font-bold">
                    {successMessage}
                  </p>
                )}

                <div className="pt-4 space-y-4">
                  <button
                    type="submit"
                    disabled={isSubmittingReset}
                    className="w-full bg-white text-black py-4 text-xs font-bold uppercase tracking-[0.2em] hover:bg-gray-200 transition-colors rounded-xl"
                  >
                    {isSubmittingReset ? "Sending..." : "Send Reset Link"}
                  </button>

                  <div className="text-center">
                    <button 
                      type="button" 
                      onClick={() => handleModeChange("login")} 
                      className="text-[10px] uppercase tracking-widest text-gray-400 hover:text-white transition-colors"
                    >
                      Back to Login
                    </button>
                  </div>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Verification Modal */}
      {showVerificationModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 w-full max-w-sm text-center">
            <h3 className="text-lg font-bold text-white uppercase tracking-widest mb-4">
              Email Verification
            </h3>

            <div className="space-y-3 text-xs text-gray-400 leading-relaxed mb-8">
              <p>Account <span className="text-white">{verificationEmail || "you"}</span> is not verified.</p>
              <p>Please check your inbox or spam folder for the verification email.</p>
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={handleResendVerification}
                disabled={isResending}
                className="w-full bg-white text-black py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-gray-200 transition-colors rounded-xl"
              >
                {isResending ? "Sending..." : "Resend Email"}
              </button>
              <button
                onClick={() => setShowVerificationModal(false)}
                className="w-full bg-transparent border border-gray-700 text-gray-400 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-gray-800 hover:text-white transition-colors rounded-xl"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        input[type="password"]::-ms-reveal,
        input[type="password"]::-ms-clear {
          display: none;
        }
        input[type="password"]::-webkit-password-reveal-button {
          display: none;
          -webkit-appearance: none;
        }
        input:-webkit-autofill,
        input:-webkit-autofill:hover, 
        input:-webkit-autofill:focus, 
        input:-webkit-autofill:active{
            -webkit-background-clip: text;
            -webkit-text-fill-color: #ffffff;
            transition: background-color 5000s ease-in-out 0s;
            box-shadow: inset 0 0 20px 20px #23232329;
        }
      `}</style>
    </div>
  );
};

export default Login;
